import { animate, style, transition, trigger } from '@angular/animations';
import {
  CdkDragDrop,
  moveItemInArray,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import _ from 'lodash';
import { filter, Observable, tap } from 'rxjs';
import { QuestionModel } from 'src/app/api/models';
import {
  AppState,
  QuestionType,
  questionTypeDisplayFn,
} from 'src/app/app.types';
import { DialogService } from '../../common/dialog.service';
import { CmsLiveEventActions } from '../../live-event/cms-live-event.actions';
import { Panel } from '../../panel/panel.types';
import { MatDialog } from '@angular/material/dialog';
import {
  ImportContentDialogComponent,
  ImportContentDialogData,
} from '../../common/import-content-dialog/import-content-dialog.component';
import { EventsPanel } from '../../panel/panels/events-panel';
import {
  EventQuestionPanel,
  getWizardEventQuestionImports,
} from '../../panel/panels/event-question-panel';
import { PanelService } from '../../panel/panel.service';
import { getEventQuestionImports } from '../../panel/panels/event-question-panel';
import {
  ExamQuestionPanel,
  getExamQuestionsImports,
  getWizardExamQuestionsImports,
} from '../../panel/panels/exam-question-panel';
import { ExamPanel } from '../../panel/panels/exam-panel';
import * as fileSaver from 'file-saver';
import { ImportEntity, WizardImports } from '../../list/list.types';
import { ImportSnackBarComponent } from '../../panel/import-snack-bar.component';
import {
  ExamsService,
  LiveEventsService,
  QuestionsService,
} from 'src/app/api/services';
import {
  ImportResultBottomSheetComponent,
  ImportResultBottomSheetData,
} from '../../panel/import-result-bottom-sheet.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NgScrollbar } from 'ngx-scrollbar';
import { ContentSpinnerComponent } from '../../common/ui/content-spinner.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { NgTemplateOutlet, NgClass, LowerCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem } from '@angular/material/list';
import { MatMenuButtonComponent } from '../../common/mat-menu-button/mat-menu-button.component';
import { MatIconButton } from '@angular/material/button';
import { QuestionEditComponent } from '../question-edit.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { LIST_ITEM_HEIGHT, validateQuestions } from 'src/app/utility/app.utils';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { AddContentWizardComponent } from '../../common/add-content-wizard/add-content-wizard.component';
import { AddContentDialogComponent } from '../../common/dialogs/add-content-dialog/add-content-dialog.component';
import { FormInputComponent } from '../../common/form-field/input.component';

@Component({
  selector: 'qa-live-question-setup',
  templateUrl: './live-question-setup.component.html',
  styleUrls: ['./live-question-setup.component.scss'],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('100ms linear', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('100ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
  imports: [
    NgScrollbar,
    ContentSpinnerComponent,
    ButtonComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    NgTemplateOutlet,
    MatIcon,
    MatList,
    CdkDropList,
    MatListItem,
    CdkDrag,
    NgClass,
    CdkDragHandle,
    CdkDragPlaceholder,
    MatMenuButtonComponent,
    MatIconButton,
    QuestionEditComponent,
    TranslateModule,
    MatCheckbox,
    LowerCasePipe,
    AddContentWizardComponent,
    FormInputComponent,
  ],
})
export class LiveQuestionSetupComponent implements OnInit, OnChanges {
  @HostBinding('@fade') fade = true;
  @Input() questions: QuestionModel[];
  @Input() allowPollQuestions: boolean;
  @Input() queryParams: { [key: string]: any };
  @Input() repositionCallback: (
    event: CdkDragDrop<QuestionModel>
  ) => Observable<void>;
  @Input() removeCallback: (questionId: number) => Observable<void>;
  @Input() translationKey: string = 'liveEvents';

  @Output() questionsUpdated = new EventEmitter<void>();
  @Input() isLiveEvent: boolean = false;
  @Input() isScheduledExam: boolean = false;
  @Input() scheduleForm: UntypedFormGroup;
  @Output() durationValueUpdate = new EventEmitter<number>();
  questionTypeDisplayFn = questionTypeDisplayFn;
  selectedQuestion: QuestionModel;
  isLoadingLeft = true;
  foldedSidebar = true;
  selectedQuestionid;
  courseId;
  panels: Array<Panel<any>>;
  /* TODO: 
    It used in books module which has old import options design.
    so this imports will be removed in future.
  */
  imports: ImportEntity<QuestionModel>[];
  wizardImports: WizardImports;
  selectedItemIds: number[] = [];
  LIST_ITEM_HEIGHT = LIST_ITEM_HEIGHT;

  constructor(
    private elementRef: ElementRef,
    private router: Router,
    activatedRoute: ActivatedRoute,
    private translator: TranslateService,
    private dialog: DialogService,
    private matSnackBar: MatSnackBar,
    private store: Store<AppState>,
    private matDialog: MatDialog,
    private eventsPanel: EventsPanel,
    private eventsQuestionPanel: EventQuestionPanel,
    private panelsService: PanelService,
    private examPanel: ExamPanel,
    private examQuestionsPanel: ExamQuestionPanel,
    private liveEventsService: LiveEventsService,
    private examsService: ExamsService,
    private questionsService: QuestionsService,
    private matBottomSheet: MatBottomSheet,
    private trackingService: TrackingService
  ) {
    this.elementRef.nativeElement.classList.add('qa-live-question-setup');

    this.panels = [
      this.eventsPanel,
      this.eventsQuestionPanel,
      this.examPanel,
      this.examQuestionsPanel,
    ];
    this.panelsService.init(this.panels);

    activatedRoute.queryParams.subscribe((params) => {
      if (params.courseId || params.bookId) {
        this.courseId = params.courseId || params.bookId;
      }
    });
  }

  ngOnInit(): void {
    this.loadImports();
    this.panelsService.importChatGPT$.subscribe((r) => {
      if (r) {
        this.questionsUpdated.emit();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.questions) {
      this.isLoadingLeft = false;
    }
  }

  foldSidebar(event) {
    event.stopPropagation();
    this.closeSideMenu();
  }

  closeSideMenu(removeQuestionFromList = false) {
    this.foldedSidebar = !this.foldedSidebar;
    if (removeQuestionFromList) {
      this.questions = this.questions.filter(
        (q) => q.id !== this.selectedQuestion.id
      );
    }
    if (this.foldedSidebar) {
      this.selectedQuestion = null;
      this.selectedQuestionid = null;
    }
  }

  afterItemDropped(event: CdkDragDrop<QuestionModel>) {
    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
    this.repositionCallback(event).subscribe({
      next: () => this.setCourseUpdated(),
    });
    this.selectedQuestionid = null;
  }

  onselectQuestion(question) {
    this.selectedQuestion = question;
    this.foldedSidebar = false;
    this.selectedQuestionid = this.questions.findIndex(
      (x) => x.id === question.id
    );
  }

  addQuestion(isPoll?: boolean) {
    const query = _.cloneDeep(this.queryParams);
    if (isPoll) {
      query['isPoll'] = true;
    }
    this.router.navigate(['/cms/add/question'], { queryParams: query });
  }

  editQuestion(event, questionId: number) {
    event.stopPropagation();
    this.router.navigate(['/cms/edit/question', questionId], {
      queryParams: this.queryParams,
    });
  }

  getItemSelectedText(singular: boolean) {
    return (
      this.translator.instant('common.label.question') +
      `${singular ? '' : this.translator.instant('common.label.n')} ` +
      this.translator.instant('common.label.selected')
    );
  }

  selectAll(checked: boolean) {
    if (checked) {
      this.questions.forEach((q) => this.selectedItemIds.push(q.id));
    } else {
      this.selectedItemIds = [];
    }
  }

  get someChecked(): boolean {
    return (
      this.selectedItemIds &&
      this.questions.length > this.selectedItemIds.length &&
      this.selectedItemIds.length > 0
    );
  }

  get isAllChecked(): boolean {
    return (
      this.selectedItemIds &&
      this.questions.length === this.selectedItemIds.length &&
      this.selectedItemIds.length > 0
    );
  }

  deleteAllSelected() {
    this.dialog
      .confirm({
        title: this.translator.instant(
          `${this.translationKey}.label.removeQuestion`
        ),
        text: this.translator.instant(
          `${this.translationKey}.label.removeFromInfo`
        ),
        buttons: [
          {
            text: this.translator.instant('common.button.abort'),
            value: false,
            color: 'warn',
          },
          {
            text: this.translator.instant('common.button.remove'),
            value: true,
            color: 'primary',
          },
        ],
      })
      .pipe(
        filter((isConfirmed) => isConfirmed),
        tap(() => (this.isLoadingLeft = true))
      )
      .subscribe(() => {
        if (this.isLiveEvent) {
          this.liveEventsService
            .removeQuestionFromEvent({
              id: this.queryParams.liveEventId,
              question_ids: this.selectedItemIds.join(','),
            })
            .subscribe({
              next: () => {
                this.matSnackBar.open(
                  this.translator.instant(
                    `${this.translationKey}.message.deleteQuestion`
                  )
                );
                this.isLoadingLeft = false;
                this.selectedItemIds = [];
                this.updateQuestions();
              },
              error: () => {
                this.matSnackBar.open(
                  this.translator.instant(
                    `${this.translationKey}.message.deleteQuestionError`
                  )
                );
              },
            });
        } else {
          this.examsService
            .removeQuestionFromExam({
              id: this.queryParams.exam,
              question_ids: this.selectedItemIds.join(','),
            })
            .subscribe({
              next: () => {
                this.matSnackBar.open(
                  this.translator.instant(
                    `${this.translationKey}.message.deleteQuestion`
                  )
                );
                this.isLoadingLeft = false;
                this.selectedItemIds = [];
                this.updateQuestions();
              },
              error: () => {
                this.matSnackBar.open(
                  this.translator.instant(
                    `${this.translationKey}.message.deleteQuestionError`
                  )
                );
              },
            });
        }
      });
  }

  loadImports() {
    let imports: ImportEntity<QuestionModel>[] = null;
    let wizardImports: WizardImports = null;
    if (this.queryParams.liveEventId) {
      /* TODO: 
        It used in books module which has old import options design.
        so this imports will be removed in future.
      */
      imports = getEventQuestionImports(
        this.translator,
        this.panelsService,
        this.matDialog,
        this.panels[1],
        !!this.courseId,
        this.queryParams.liveEventId,
        true,
        this.trackingService
      );
      wizardImports = getWizardEventQuestionImports(
        this.translator,
        this.panelsService,
        this.matDialog,
        this.panels[1],
        !!this.courseId,
        this.queryParams,
        true,
        this.trackingService,
        this.router
      );
    } else if (this.queryParams.exam) {
      /* TODO: 
        It used in books module which has old import options design.
        so this imports will be removed in future.
      */
      imports = getExamQuestionsImports(
        this.translator,
        this.panelsService,
        this.matDialog,
        this.panels[3],
        !!this.courseId,
        this.queryParams.exam,
        true,
        this.trackingService
      );
      wizardImports = getWizardExamQuestionsImports(
        this.translator,
        this.panelsService,
        this.matDialog,
        this.panels[3],
        !!this.courseId,
        this.queryParams,
        true,
        this.trackingService,
        this.router
      );
    }
    this.imports = imports;
    if (wizardImports?.importButtons?.length) {
      const fIdx = wizardImports?.importButtons.findIndex(
        (im) => im.id === 'import-json'
      );
      if (fIdx !== -1) {
        wizardImports.importButtons[fIdx].click = () => {
          this.importQuestionFromJson(!!this.queryParams.exam);
        };
      }
    }
    this.wizardImports = wizardImports;
  }

  handleAfterClose() {
    this.questionsUpdated.emit();
    // this is used to update the questions list when new added
    setTimeout(() => {
      this.panelsService.importComplete$.next(true);
    }, 1000);
  }

  /* TODO: 
    It used in books module which has old import options design.
    so this importItem() will be removed in future.
  */
  importItem() {
    if (!this.imports?.length) return;
    const fIdx = this.imports.findIndex((im) => im.id === 'import-json');
    if (fIdx !== -1) {
      this.imports[fIdx].action.click = () => {
        this.importQuestionFromJson(!!this.queryParams.exam);
      };
    }

    const dialogRef = this.matDialog.open<
      ImportContentDialogComponent,
      ImportContentDialogData
    >(ImportContentDialogComponent, {
      data: {
        imports: this.imports,
        dataSource: null,
        title: this.translator.instant('content.exam.questionImport'),
      },
      maxHeight: '90vh',
    });
    dialogRef.afterClosed().subscribe(() => this.handleAfterClose());
  }

  openAddContentDialog() {
    const dialogRef = this.matDialog.open<AddContentDialogComponent>(
      AddContentDialogComponent,
      {
        data: {
          wizardImports: this.wizardImports,
          dataSource: null,
          title: this.translator.instant(
            'content.wizardActions.title.createQuestion'
          ),
        },
        autoFocus: false,
        maxHeight: '90vh',
        panelClass: 'add-content-dialog-panel-class',
      }
    );
    dialogRef.afterClosed().subscribe(() => this.handleAfterClose());
  }

  async importQuestionFromJson(isExam = false) {
    const selectedQuestion = (await this.panelsService.promptFile(
      'json'
    )) as QuestionModel;
    if (selectedQuestion) {
      const importProgress = {
        current: 0,
        total: 1,
      };
      const snackBarRef = this.matSnackBar.openFromComponent(
        ImportSnackBarComponent,
        {
          data: {
            progress: importProgress,
            message: 'content.exam.questionImport',
          },
          duration: null,
        }
      );
      const sentryInfo = {
        title: `Error while create question`,
        file: 'question-list.component.ts',
        api: 'createQuestion',
        preventLogs: true,
      };
      const { isValid } = validateQuestions(
        sentryInfo,
        [selectedQuestion],
        this.translator,
        this.matSnackBar
      );
      if (!isValid) {
        return;
      }
      if (isExam) {
        await this.examsService
          .copyQuestionToExam({
            id: this.queryParams.exam,
            body: [selectedQuestion],
          })
          .toPromise();
      } else {
        if (selectedQuestion.type === QuestionType.FREETEXT) {
          selectedQuestion.is_poll_question = true;
        }
        await this.liveEventsService
          .copyQuestionToEvent({
            id: this.queryParams.liveEventId,
            body: [selectedQuestion],
          })
          .toPromise();
      }
      importProgress.current += 1;
      snackBarRef.dismiss();
      this.matDialog.closeAll();
      this.handleAfterClose();
      const bottomSheetData: ImportResultBottomSheetData = {
        info: {
          current: importProgress.current,
          total: importProgress.total,
          failedItems: [],
        },
      };
      this.matBottomSheet.open(ImportResultBottomSheetComponent, {
        data: bottomSheetData,
      });
    }
  }

  openGPT() {
    const gptImport = this.imports.find((imp) => imp.id === 'chat-gpt');
    if (gptImport) {
      gptImport.action.click();
    }
  }

  exportItemAsJSON(question: QuestionModel) {
    const exportQuestion = JSON.parse(JSON.stringify(question));
    delete exportQuestion.id;
    const panel = this.queryParams.liveEventId
      ? this.eventsQuestionPanel
      : this.examQuestionsPanel;
    exportQuestion['meta:importType'] = panel.type;
    const file = `${JSON.stringify(exportQuestion)}\n`;
    const blob = new Blob([file], { type: 'application/json' });
    fileSaver.saveAs(
      blob,
      `qa-${panel.type}-${exportQuestion[panel.itemTextProperty]}.json`
    );
  }

  removeQuestion(questionId: number) {
    this.dialog
      .confirm({
        title: this.translator.instant(
          `${this.translationKey}.label.removeQuestion`
        ),
        text: this.translator.instant(
          `${this.translationKey}.label.removeFromInfo`
        ),
        buttons: [
          {
            text: this.translator.instant('common.button.abort'),
            value: false,
            color: 'warn',
          },
          {
            text: this.translator.instant('common.button.remove'),
            value: true,
            color: 'primary',
          },
        ],
      })
      .pipe(
        filter((isConfirmed) => isConfirmed),
        tap(() => (this.isLoadingLeft = true))
      )
      .subscribe(() => {
        this.removeCallback(questionId).subscribe({
          next: () => {
            this.matSnackBar.open(
              this.translator.instant(
                `${this.translationKey}.message.deleteQuestion`
              )
            );
            this.isLoadingLeft = false;
            this.setCourseUpdated();
          },
          error: () => {
            this.matSnackBar.open(
              this.translator.instant(
                `${this.translationKey}.message.deleteQuestionError`
              )
            );
          },
        });
      });
  }

  onDelete(questionId: number) {
    this.questionsService
      .deleteQuestion({
        id: questionId,
      })
      .subscribe({
        next: () => {
          this.questionsUpdated.emit();
          this.matSnackBar.open(
            this.translator.instant(
              `${this.translationKey}.message.deleteQuestion`
            )
          );
          this.isLoadingLeft = false;
          this.setCourseUpdated();
        },
        error: () => {
          this.matSnackBar.open(
            this.translator.instant(
              `${this.translationKey}.message.deleteQuestionError`
            )
          );
        },
      });
  }

  deleteQuestion(questionId: number) {
    this.dialog
      .confirm({
        title: this.translator.instant(
          `${this.translationKey}.label.deleteQuestion`
        ),
        text: this.translator.instant(
          `${this.translationKey}.label.deleteFromInfo`
        ),
        buttons: [
          {
            text: this.translator.instant('common.button.abort'),
            value: false,
            color: 'warn',
          },
          {
            text: this.translator.instant('liveEvents.label.delete'),
            value: true,
            color: 'primary',
          },
        ],
      })
      .pipe(
        filter((isConfirmed) => isConfirmed),
        tap(() => (this.isLoadingLeft = true))
      )
      .subscribe(() => {
        this.onDelete(questionId);
      });
  }

  updateQuestions() {
    if (this.queryParams.liveEventId) {
      this.store.dispatch(
        CmsLiveEventActions.updateQuestions({
          liveEventId: this.queryParams.liveEventId,
          initEvent: true,
        })
      );
    } else {
      this.questionsUpdated.emit();
    }
    this.setCourseUpdated();
  }

  setCourseUpdated() {
    if (this.courseId) {
      this.panelsService.courseUpdated$.next(true);
    }
  }

  onDurationValueChange(event) {
    this.durationValueUpdate.emit(event);
  }
}
